import './Settings.css';
import '../Modal/Modal.css';
import Navbar from '../Navigation/Navbar';
import SpecializationSelector from '../PopUp/SpecializationSelector';
import ReportForm from './ReportForm';

import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isAuthenticated, getUserSettings, updateUserSettings } from '../../api/users_institutions';
import {getCurrentUser, updateUser, changePassword} from '../../api/users_institutions';
import { getAllInstitutions } from '../../api/users_institutions';
import { getAllUserInstitutions, createUserInstitution, deleteUserInstitution } from '../../api/users_institutions';
import { getUserSpecializations, createUserSpecialization, updateUserSpecialization, deleteUserSpecialization } from '../../api/users_institutions';
import { getAllCategories } from '../../api/categories';
import { getAllLevels, getLevelDomains } from '../../api/levels';
import { isLevelLockedForUser } from '../../utils/levelAccess';
import { sendVerificationEmail, verifyEmailCode } from '../../api/mail_verification';
import { GRADE_GROUPS, useSpecializationGrades } from '../../hooks/useSpecializationGrades';
import { AVAILABLE_NOTIF_SOUNDS, previewNotificationSound } from '../../utils/notificationSound';
import SavingIndicator from '../Common/SavingIndicator';

const getPasswordConstraintError = (password, t) => {
    if (password.length < 8) {
        return t('common.passwordMinLength');
    }
    // More Restrictions Disabled for now.
    // if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
    //     return 'Password must include both uppercase and lowercase letters.';
    // }
    // if (!/\d/.test(password)) {
    //     return 'Password must include at least one number.';
    // }
    // if (!/[^A-Za-z0-9]/.test(password)) {
    //     return 'Password must include at least one special character.';
    // }
    return '';
};

const LockIcon = ({ size = 18 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-label="Locked module"
        role="img"
    >
        <title>Locked module</title>
        <rect x="3" y="10" width="18" height="11" rx="2.5" fill="currentColor" fillOpacity="0.12" />
        <path d="M7 10V7a5 5 0 0 1 10 0v3" />
        <circle cx="12" cy="15" r="1.25" fill="currentColor" stroke="none" />
        <path d="M12 16.25V18" />
        <path d="M5.5 10h13" opacity="0.45" />
    </svg>
);

function Settings() {
    const { t, i18n } = useTranslation();
    const userIsAuthenticated = isAuthenticated();
    const currentUser = getCurrentUser();
    const navigate = useNavigate();
    const location = useLocation();

    const [currentTheme, setCurrentTheme] = useState('light');
    const [notifSound, setNotifSound] = useState('notif1');
    const [muteNotifications, setMuteNotifications] = useState(false);
    const [loading, setLoading] = useState(true);
    // Covers the three UserSettings PATCHes below (theme/notif_sound/mute_notification) -- the
    // visual change (data-theme attribute, dropdown/switch position) is deferred until the
    // backend actually confirms the write, rather than applied optimistically, so there's
    // nothing for a later effect to race against and briefly revert.
    const [isSavingPreferences, setIsSavingPreferences] = useState(false);
    // Set by whichever nav link/click routed here (see Navbar.jsx, DashboardGrid.jsx) via
    // navigate(..., { state: { from } }) -- document.referrer can't be used for this anymore
    // since those now navigate client-side rather than doing a full page load.
    const closePath = location.state?.from || '/dashboard';

    /* Section Navigation -- a sidebar of links next to a single content panel, rather than a
       grid of separately-boxed cards. Every section stays mounted (only display is toggled), so
       DOM ids the imperative handlers below rely on (newEmailInput, emailAlert, etc.) always
       exist, and in-progress input isn't lost when switching tabs. */
    const SD_SECTIONS = [
        { id: 'profile', label: t('settings.profileDetails') },
        { id: 'emails', label: t('settings.emailManagement') },
        { id: 'content', label: t('settings.unlockedContent') },
        { id: 'grades', label: t('settings.manageGrades') },
        { id: 'preferences', label: t('settings.advancedSettings') },
        { id: 'reports', label: t('settings.reports') },
    ];
    const [activeSection, setActiveSection] = useState('profile');

    useEffect(() => {
        if (userIsAuthenticated && currentUser) {
            // Load user settings
            loadSettings();
        }
        // currentUser.id (not currentUser itself) -- getCurrentUser() re-parses localStorage into
        // a brand-new object on every render, so depending on the object reference made this
        // effect (and its loadSettings() GET) re-fire on every single re-render, including the
        // one triggered by handleThemeChange's own setCurrentTheme. That GET would occasionally
        // land *after* that render but *before* the matching PATCH committed server-side, so it
        // read back the pre-change value and reverted the theme that was just applied -- the
        // "flash back to light" this depended on a stable primitive was meant to prevent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIsAuthenticated, currentUser?.id]);

    /* User Profile Settings*/

    const [userAttributes, setUserAttributes] = useState({
        first_name: currentUser?.first_name || '',
        last_name: currentUser?.last_name || '',
        profile_picture: currentUser?.profile_picture || null,
    });
    const [profilePreviewSrc, setProfilePreviewSrc] = useState(currentUser?.profile_picture_url || currentUser?.profile_picture || '');
    const [isUploadingPfp, setIsUploadingPfp] = useState(false);

    const loadSettings = async () => {
        try {
            const settings = await getUserSettings(currentUser.id);
            setCurrentTheme(settings.theme);
            document.documentElement.setAttribute('data-theme', settings.theme);
            setNotifSound(settings.notif_sound || 'notif1');
            setMuteNotifications(!!settings.mute_notification);
        } catch (error) {
            console.error('Error loading settings:', error);
            // Default to light theme if error
            setCurrentTheme('light');
        } finally {
            setLoading(false);
        }
    };

    const loadUser = async () => {
        try {
            const user = await getCurrentUser();
            setUserAttributes({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                profile_picture: user.profile_picture || null,
            });
            setProfilePreviewSrc(user.profile_picture_url || user.profile_picture || '');
        } catch (error) {
            console.error('Error loading user:', error);
        }
    }

    const saveProfile = async () => {
        if (isUploadingPfp) return;

        const btn = document.getElementById('sdBtnSaveProfile');
        const isUploadingNewPicture = userAttributes.profile_picture instanceof File;
        if (isUploadingNewPicture) setIsUploadingPfp(true);

        try {
            const formData = new FormData();
            formData.append('first_name', userAttributes.first_name);
            formData.append('last_name', userAttributes.last_name);

            if (userAttributes.profile_picture instanceof File) {
                formData.append('profile_picture', userAttributes.profile_picture);
            } else if (userAttributes.profile_picture === null) {
                formData.append('profile_picture', '');
            }

            await updateUser(currentUser.id, formData);
            btn.innerText = t('settings.saved');
            btn.style.background = 'var(--success)';
            btn.disabled = true;
            await loadUser(); // Refresh user data (and profilePreviewSrc with the now-hosted R2 URL) after update
            setTimeout(() => {btn.innerText = t('settings.saveChanges'); btn.disabled = false; btn.style.background = 'var(--accent)'}, 2000);
        } catch (error) {
            console.error('Error updating user:', error);
            btn.innerText = t('settings.errorSaving');
            btn.style.background = 'var(--error)';
            btn.disabled = true;
            setTimeout(() => {btn.innerText = t('settings.saveChanges'); btn.disabled = false; btn.style.background = 'var(--accent)'}, 2000);
        } finally {
            if (isUploadingNewPicture) setIsUploadingPfp(false);
        }
    };

    const updateAttributes = (field, value) => {
        setUserAttributes(prev => ({ ...prev, [field]: value }));
    };

    const handlePfpChange = (input) => {
        if (input?.target?.files?.[0]) {
            const reader = new FileReader();
            const file = input.target.files[0];

            try {
                reader.onload = function(e) {
                    setProfilePreviewSrc(e.target?.result || '');
                }
                reader.readAsDataURL(file);
                updateAttributes('profile_picture', file);
            } catch (error) {
                console.error('Error loading image:', error);
            }
            input.target.value = '';
        }
    }

    const handlePfpRemoval = () => {
        setProfilePreviewSrc('');
        updateAttributes('profile_picture', null);
    }

    /* User Institution Management */

    const [institutions, setInstitutions] = useState([]);
    const [userSpecializations, setUserSpecializations] = useState([]);
    const [allUserInstitutions, setAllUserInstitutions] = useState([]);

    const [specializationSelectorOpen, setSpecializationSelectorOpen] = useState(false);
    const [specializationSelectorForceStage, setSpecializationSelectorForceStage] = useState(undefined);

    const hasSchoolSpecialization = userSpecializations.some(
        (spec) => String(spec.target_domain || '').trim().toLowerCase() !== 'university'
    );

    const openSpecializationAutoModal = () => {
        setSpecializationSelectorForceStage(undefined);
        setSpecializationSelectorOpen(true);
    };

    const closeSpecializationSelector = () => {
        setSpecializationSelectorOpen(false);
        fetchUserSpecializations();
        loadCurrentGradeSelections();
    };

    /* Inline grade picker (Manage Grades section) -- shares its save logic with
       SpecializationSelector.jsx via useSpecializationGrades so the two can't diverge. Editing
       here just stages a selection; clicking Save & Continue hands it to the modal's confirmation
       screen (forceStage="verify") rather than writing to the backend directly. */
    const {
        selectedGroups: gradesSelectedGroups,
        toggleGrade: toggleGradeSelection,
        totalSelectedGrades,
        loadCurrentGrades: loadCurrentGradeSelections,
    } = useSpecializationGrades();
    const [gradesErrorMessage, setGradesErrorMessage] = useState('');

    useEffect(() => {
        if (hasSchoolSpecialization) {
            loadCurrentGradeSelections();
        }
    }, [hasSchoolSpecialization, loadCurrentGradeSelections]);

    const handleGradesSaveContinue = () => {
        if (totalSelectedGrades === 0) {
            setGradesErrorMessage(t('specializationSelector.selectAtLeastOne'));
            return;
        }
        setGradesErrorMessage('');
        setSpecializationSelectorForceStage('verify');
        setSpecializationSelectorOpen(true);
    };

    const getInstitutionForEmail = useCallback((email) => {
        const domain = String(email || '').split('@')[1]?.toLowerCase();
        if (!domain) {
            return null;
        }

        return institutions.find((inst) => String(inst.domain || '').toLowerCase() === domain) || null;
    }, [institutions]);

    const getInstitutions = async () => {
        const insts = await getAllInstitutions();
        setInstitutions(insts);
    }
    const fetchUserSpecializations = async () => {
        if (!currentUser?.id) {
            setUserSpecializations([]);
            return;
        }

        try {
            const specs = await getUserSpecializations(currentUser.id);
            setUserSpecializations(Array.isArray(specs) ? specs : []);
        } catch (error) {
            console.error('Error loading user specializations:', error);
            setUserSpecializations([]);
        }
    };
    const fetchUserInstitutions = async () => {
        const userInsts = await getAllUserInstitutions(currentUser.id);
        setAllUserInstitutions(userInsts);
    }

    const getUserInstitutions = () => {
        const userInsts = allUserInstitutions.filter(ui => ui.user === currentUser.id);
        return userInsts;
    }

    useEffect(() => {
        getInstitutions();
        fetchUserInstitutions();
        fetchUserSpecializations();
    }, []);

    /* User Institution Mail Management*/

    const [pendingEmailToVerify, setPendingEmailToVerify] = useState(null);
    const [emailToRemove, setEmailToRemove] = useState(null);

    const initiateEmailLink = () => {
        const emailInput = document.getElementById('newEmailInput').value.trim().toLowerCase();
        const alertBox = document.getElementById('emailAlert');

        alertBox.style.display = 'none';

        if (!emailInput || !emailInput.includes('@')) {
            showAlert(t('settings.errors.invalidEmail'), 'emailAlert');
            return;
        }

        if (getUserInstitutions().some(ui => ui.email === emailInput)) {
            showAlert(t('settings.errors.alreadyLinkedYourAccount'), 'emailAlert');
            return;
        }

        fetchUserInstitutions();
        if (allUserInstitutions.some(ui => ui.email === emailInput && !(ui.user === currentUser.id))) {
            showAlert(t('settings.errors.alreadyLinkedAnotherAccount'), 'emailAlert');
            return;
        }

        const domain = emailInput.split('@')[1];

        if (institutions.some(inst => inst.domain === domain && getUserInstitutions().some(ui => ui.institution === inst.id))) {
            showAlert(t('settings.errors.domainAlreadyVerified'), 'emailAlert');
            return;
        }

        if (institutions.some(inst => inst.domain === domain)) {
            setPendingEmailToVerify(emailInput);
            document.getElementById('verifyEmailTarget').innerText = emailInput;
            sendVerificationEmail(emailInput, i18n.language).catch((err) => {
                console.error('Failed to send verification email:', err);
            });
            openModal('modalVerifyEmail');
        } else {
            showAlert(t('settings.errors.domainNotFound', { domain }), 'emailAlert');
        }

    }

    const moveToNext = (current, nextFieldID) => {
        if (current && current.value && current.value.length >= 1) {
            if (nextFieldID) {
                const nextField = document.getElementById(nextFieldID);
                if (nextField) {
                    nextField.focus();
                }
            }
        }
    }

    const startVerificationProcess = async () => {
        let enteredCode = "";
        for(let i=1; i<=6; i++) {
            enteredCode += document.getElementById('c' + i).value;
        }

        // 1. Hide the input modal, show the loading modal
        document.getElementById('modalVerifyEmail').style.display = 'none';

        const loadModal = document.getElementById('modalLoading');
        const stateLoading = document.getElementById('loadingState');
        const stateSuccess = document.getElementById('successState');
        const stateError = document.getElementById('errorState');

        stateLoading.style.display = 'block';
        stateSuccess.style.display = 'none';
        stateError.style.display = 'none';
        loadModal.style.display = 'flex';

        // 2. Verify the code against the backend
        let verified = false;
        try {
            const result = await verifyEmailCode(pendingEmailToVerify, enteredCode);
            verified = !!result?.verified;
        } catch (err) {
            console.error('Failed to verify code:', err);
        }

        stateLoading.style.display = 'none';

        if (verified) {
            // SUCCESS
            stateSuccess.style.display = 'block';

            setTimeout(() => {
                loadModal.style.display = 'none';
                executeSuccessfulLink();
            }, 1500);

        } else {
            // ERROR
            stateError.style.display = 'block';

            setTimeout(() => {
                loadModal.style.display = 'none';
                clearVerificationInputs();
                document.getElementById('modalVerifyEmail').style.display = 'flex';
                document.getElementById('c1').focus();
            }, 2000);
        }
    }

    const executeSuccessfulLink = async () => {
        try {
            const institution = institutions.find(inst => pendingEmailToVerify.endsWith('@' + inst.domain));
            const formdata = new FormData();
            formdata.append('user', currentUser.id);
            formdata.append('email', String(pendingEmailToVerify).trim());
            formdata.append('institution', institution.id);
            formdata.append('verified', 'true');

            await createUserInstitution(formdata);

            await syncUserSpecializationsForEmails(currentUser, [...getUserInstitutions().map(ui => ui.email), pendingEmailToVerify]);
            await fetchUserSpecializations();
            openSpecializationAutoModal();
        } catch (error) {
            console.error('Error linking email:', error);
        }
        fetchUserInstitutions();
        setPendingEmailToVerify(null);

        document.getElementById('newEmailInput').value = '';
        clearVerificationInputs();
    }


    const promptRemoveEmail = (uiID) => {
        const emails = getUserInstitutions();
        if (emails.length === 1) {
            showAlert(t('settings.errors.mustHaveOneEmail'), 'emailAlert');
            return;
        }
        const email = emails.find(ui => ui.id === uiID)?.email || 'this email';
        setEmailToRemove(uiID);
        document.getElementById('removeEmailTarget').innerText = email;
        openModal('modalConfirmRemove');
    }

    const executeRemoveMail = async (uiId) => {
        try {
            await deleteUserInstitution(uiId);
            fetchUserInstitutions();
            fetchUserSpecializations();
            setEmailToRemove(null);
            closeModal('modalConfirmRemove');
        } catch (error) {
            console.error('Error removing email:', error);
        }
    }

    /* Password Change Feature */
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handlePasswordChange = async () => {
        if (passwords.newPassword !== passwords.confirmPassword) {
            showAlert(t('settings.errors.passwordsNoMatch'), 'passwordAlert');
            return;
        }

        const passwordError = getPasswordConstraintError(passwords.newPassword, t);
        if (passwordError) {
            showAlert(passwordError, 'passwordAlert');
            return;
        }


        try {
            await changePassword(passwords.currentPassword, passwords.newPassword);
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            console.error('Error changing password:', error);
            showAlert(t('settings.errors.failedChangePassword'), 'passwordAlert');
        }
    };

    /* Modal Management */

    const openModal = (id) => { document.getElementById(id).style.display = 'flex'; };

    const closeModal = (id) => {
        document.getElementById(id).style.display = 'none';
        clearVerificationInputs();
    };
    const clearVerificationInputs = () => {
        document.querySelectorAll('.code-input').forEach(input => input.value = '');
    };

    /* Module Management*/

    // Every level sitting in a "teachers"-audience category -- the modules an educator can be
    // granted. Fetched alongside the levels' LevelDomain rows so each card can say whether this
    // user's specializations actually unlock it, using the same formula as the dashboard's
    // level-card lock icon (isLevelLockedForUser) so the two views can't disagree.
    const [allModules, setAllModules] = useState([]);
    const [levelDomains, setLevelDomains] = useState([]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([getAllLevels(), getAllCategories(), getLevelDomains()])
            .then(([levels, categories, domains]) => {
                if (cancelled) return;
                const teacherCategoryIds = new Set(
                    categories
                        .filter((category) => category.audience === 'teachers')
                        .map((category) => category.id)
                );
                setAllModules(
                    levels
                        .filter((level) => teacherCategoryIds.has(level.category))
                        .map((level) => ({ id: level.id, name: level.name, icon_url: level.icon_url, icon: level.icon }))
                );
                setLevelDomains(domains);
            })
            .catch((error) => {
                console.error('Error loading unlocked-content modules:', error);
            });
        return () => { cancelled = true; };
    }, []);

    const getModuleLabel = (module) => module?.name || module;

    const getRequiredSpecializationDomains = useCallback((emails) => {
        const domainsByKey = new Map();

        emails.forEach((email) => {
            const institution = getInstitutionForEmail(email);
            const targetDomain = String(institution?.type || '').trim();

            if (targetDomain) {
                domainsByKey.set(targetDomain.toLowerCase(), targetDomain);
            }
        });

        return Array.from(domainsByKey.values());
    }, [getInstitutionForEmail]);

    const syncUserSpecializationsForEmails = useCallback(async (user, emails) => {
        if (!user?.id || !Array.isArray(emails) || emails.length === 0) {
            return;
        }

        const requiredTargetDomains = getRequiredSpecializationDomains(emails);
        if (requiredTargetDomains.length === 0) {
            return;
        }

        try {
            const userSpecializations = await getUserSpecializations(user.id);
            const existingTargetDomains = new Set(
                (Array.isArray(userSpecializations) ? userSpecializations : [])
                    .map((specialization) => String(specialization.target_domain || '').trim().toLowerCase())
                    .filter(Boolean)
            );

            for (const targetDomain of requiredTargetDomains) {
                if (!existingTargetDomains.has(targetDomain.toLowerCase())) {
                    await createUserSpecialization(user.id, {
                        user: user.id,
                        target_domain: targetDomain,
                    });
                    existingTargetDomains.add(targetDomain.toLowerCase());
                }
            }
        } catch (err) {
            console.error('Failed to sync user specializations:', err);
        }
    }, [getRequiredSpecializationDomains]);


    /* Other Settings */

    // Applied only once the PATCH actually confirms -- an earlier optimistic version set
    // currentTheme/data-theme immediately, which a redundant loadSettings() GET (see the
    // useEffect above) could occasionally race and revert mid-flight, flashing back to the old
    // theme for a moment. Waiting for the response removes that window entirely.
    const handleThemeChange = async (newTheme) => {
        setIsSavingPreferences(true);
        try {
            await updateUserSettings(currentUser.id, { theme: newTheme });
            setCurrentTheme(newTheme);
            document.documentElement.setAttribute('data-theme', newTheme);
        } catch (error) {
            console.error('Error saving theme:', error);
        } finally {
            setIsSavingPreferences(false);
        }
    };

    // Picking a sound previews it immediately (regardless of the mute toggle below -- the user
    // is actively auditioning it, and a preview has no persisted state to race against) while
    // still waiting for the PATCH to confirm before the dropdown's displayed value changes.
    const handleNotifSoundChange = async (newSound) => {
        previewNotificationSound(newSound);
        setIsSavingPreferences(true);
        try {
            await updateUserSettings(currentUser.id, { notif_sound: newSound });
            setNotifSound(newSound);
        } catch (error) {
            console.error('Error saving notification sound:', error);
        } finally {
            setIsSavingPreferences(false);
        }
    };

    const handleMuteToggle = async () => {
        const newValue = !muteNotifications;
        setIsSavingPreferences(true);
        try {
            await updateUserSettings(currentUser.id, { mute_notification: newValue });
            setMuteNotifications(newValue);
        } catch (error) {
            console.error('Error saving mute setting:', error);
        } finally {
            setIsSavingPreferences(false);
        }
    };

    if (!userIsAuthenticated) {
        navigate('/');
        return null;
    }

    const showAlert = (msg, alertId) => {
        const alertBox = document.getElementById(alertId);
        alertBox.innerText = msg;
        alertBox.style.display = 'block';
    }

    return (
        <>
            <div className="Settings">
                <Navbar />

                <div className="sd-shell">
                    <div className="sd-card">
                        <aside className="sd-sidebar">
                            <div className="sd-user-mini">
                                <div className="sd-user-avatar">
                                    {profilePreviewSrc ? (
                                        <img src={profilePreviewSrc} alt="Profile" />
                                    ) : (
                                        <span>{currentUser.first_name?.charAt(0) || currentUser.username?.charAt(0) || '?'}</span>
                                    )}
                                </div>
                                <div className="sd-user-mini-text">
                                    <strong>{currentUser.first_name || currentUser.username}</strong>
                                    <span>{currentUser.username}</span>
                                </div>
                            </div>

                            <nav className="sd-nav">
                                {SD_SECTIONS.map(section => (
                                    <button
                                        key={section.id}
                                        type="button"
                                        className={`sd-nav-item ${activeSection === section.id ? 'active' : ''}`}
                                        onClick={() => setActiveSection(section.id)}
                                        disabled={section.id === 'grades' && !hasSchoolSpecialization}
                                    >
                                        {section.label}
                                    </button>
                                ))}
                            </nav>
                        </aside>

                        <div className="sd-panel">

                            <section className={`sd-section ${activeSection === 'profile' ? 'active' : ''}`}>
                                <header className="sd-panel-header">
                                    <h1>{t('settings.profileDetails')}</h1>
                                </header>

                                <div className="pfp-wrapper">
                                    <div className="banner-pfp pfp-circle" id="pfpDisplay">
                                        <img id="pfpImage" src={profilePreviewSrc || ''} alt="Profile" style={{ display: profilePreviewSrc ? 'block' : 'none' }} />
                                        {!profilePreviewSrc && <span>{currentUser.first_name?.charAt(0) || currentUser.username?.charAt(0) || '?'}</span>}
                                        {isUploadingPfp && (
                                            <div className="pfp-loading-overlay">
                                                <div className="pfp-spinner"></div>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
                                        <input type="file" id="pfpUpload" accept="image/*" style={{display: 'none'}} onChange={handlePfpChange} disabled={isUploadingPfp} />
                                        <button className="pfp-edit-btn" onClick={() => document.getElementById('pfpUpload').click()} disabled={isUploadingPfp}>{t('settings.changePicture')}</button>
                                        <button className="pfp-edit-btn" onClick={handlePfpRemoval} disabled={isUploadingPfp}>{t('settings.removePicture')}</button>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="label-text">{t('settings.firstName')}</label>
                                        <input type="text" className="login-input" defaultValue={currentUser.first_name} onChange={(e) => updateAttributes('first_name', e.target.value)} id="fName" />
                                    </div>
                                    <div className="form-group">
                                        <label className="label-text">{t('settings.lastName')}</label>
                                        <input type="text" className="login-input" defaultValue={currentUser.last_name} id="lName" onChange={(e) => updateAttributes('last_name', e.target.value)}/>
                                    </div>
                                </div>

                                <label className="label-text">{t('settings.linkedOrganizations')}</label>
                                <div className="org-logo-box" id="orgLogoBox" style={{borderStyle: 'solid', borderColor: '#e2e8f0', background: 'var(--background)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'}}>
                                    {
                                        getUserInstitutions().length === 0 ?
                                        <span className="org-logo-empty">{t('settings.noOrganizationsLinked')}</span>
                                        :
                                        institutions.filter(inst => getUserInstitutions().some(ui => ui.institution === inst.id)).map(inst => (
                                            <div className="org-logo-wrapper" key={inst.id}>
                                                {inst.logo ? (<img className="org-logo-item" src={inst.logo_url || inst.logo} alt={`${inst.name} Logo`} />) : (
                                                    <span className="org-logo-item-alt" style={{backgroundColor: inst.icon_color || '#0284c7', color: 'white'}}>{inst.name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2)}</span>
                                                )}
                                            </div>
                                        ))
                                    }
                                </div>
                                <div className="sd-profile-buttons">
                                    <button className="btn-primary sd-btn-inline" style={{marginTop: "25px"}} onClick={() => openModal("modalChangePassword")} >{t('settings.changePassword')}</button>
                                    <button id="sdBtnSaveProfile" className="btn-primary sd-btn-inline" style={{marginTop: "25px"}} onClick={() => saveProfile()}>{t('settings.saveChanges')}</button>
                                </div>
                            </section>

                            <section className={`sd-section ${activeSection === 'emails' ? 'active' : ''}`}>
                                <header className="sd-panel-header">
                                    <h1>{t('settings.emailManagement')}</h1>
                                </header>

                                <div className="email-list" id="emailList">
                                    {allUserInstitutions.length === 0 ? (
                                        <span className="email-empty">{t('settings.noLinkedEmails')}</span>
                                    ) : (
                                        getUserInstitutions().map(ui => (
                                            <div className="email-item" id="initial-email" key={ui.id}>
                                                <div style={{display:'flex', alignItems:'center', gap: '10px'}}>
                                                    <span className="email-text">{ui.email}</span>
                                                    <span className={ui.verified ? 'email-status' : 'email-status-unverified'}>
                                                        {ui.verified ? t('settings.verified') : t('settings.notVerified')}
                                                    </span>
                                                </div>
                                                <button className="btn-remove" title={t('settings.removeEmailTitle')} onClick={() => promptRemoveEmail(ui.id)}>✕</button>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <label className="label-text" style={{marginTop: '25px'}}>{t('settings.linkOrgEmailLabel')}</label>
                                <div className="add-email-row">
                                    <input type="email" id="newEmailInput" className="login-input" placeholder={t('settings.emailPlaceholder')}></input>
                                    <button className="btn-primary btn-add" onClick={initiateEmailLink}>{t('settings.sendCode')}</button>
                                </div>
                                <div id="emailAlert" className="alert-msg alert-error"></div>

                                <div className="sd-info-box">
                                    <p>
                                        <strong style={{color: 'var(--accent)'}}>{t('settings.whyLinkTitle')}</strong><br />
                                        {t('settings.whyLinkBody')}
                                    </p>
                                </div>
                            </section>

                            <section className={`sd-section ${activeSection === 'content' ? 'active' : ''}`}>
                                <header className="sd-panel-header">
                                    <h1>{t('settings.unlockedContent')}</h1>
                                    <p>{t('settings.modulesAutoGranted')}</p>
                                </header>

                                <div className="module-grid" id="moduleAccessBox">
                                    {allModules.map(module => {
                                        const isUnlocked = !isLevelLockedForUser(module.id, userSpecializations, levelDomains);
                                        const hasIcon = module.icon_url || module.icon;
                                        return (
                                            <div
                                                key={module.id} className={`module-badge module-${isUnlocked ? 'unlocked' : 'locked'} ${hasIcon ? 'has-icon' : ''}`}
                                                style={hasIcon ? {backgroundImage: `url(${module.icon_url || module.icon})`, backgroundSize: 'cover', backgroundPosition: 'center'} : {}}
                                            >
                                                {
                                                    isUnlocked ?
                                                    <>
                                                        {
                                                            !hasIcon ? (<>
                                                                <span style={{fontSize: '2.2rem', display: 'block'}}>📚</span>
                                                                <span>{getModuleLabel(module)}</span>
                                                            </>) : (<>
                                                                <span style={{color: 'white', fontWeight: 'bold'}}>{getModuleLabel(module)}</span>
                                                            </>)
                                                        }
                                                    </>
                                                    :
                                                    <>
                                                        <span style={{fontSize: '2.2rem', display: 'block', color: hasIcon ? 'white' : undefined}}><LockIcon size={25} /></span>
                                                        <span style={{color: hasIcon ? 'white' : undefined, fontWeight: hasIcon ? 'bold' : undefined}}>{getModuleLabel(module)}</span>
                                                    </>
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>

                            <section className={`sd-section ${activeSection === 'grades' ? 'active' : ''}`}>
                                <header className="sd-panel-header">
                                    <h1>{t('settings.manageGrades')}</h1>
                                    <p>{hasSchoolSpecialization ? t('settings.manageGradesDesc') : t('settings.manageGradesLockedDesc')}</p>
                                </header>

                                {hasSchoolSpecialization ? (
                                    <>
                                        <div className="grades-container">
                                            {GRADE_GROUPS.map((group) => (
                                                <div className="grade-group" key={group.domain}>
                                                    <h4>{t(group.labelKey)}</h4>
                                                    <div className="grade-pills">
                                                        {group.grades.map((grade) => (
                                                            <label className="grade-pill" key={grade}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={gradesSelectedGroups[group.domain]?.has(grade) || false}
                                                                    onChange={() => toggleGradeSelection(group.domain, grade)}
                                                                />
                                                                <span>{grade}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {gradesErrorMessage && (
                                            <div className="alert-msg alert-error" style={{ display: 'block', marginBottom: '15px'}}>{gradesErrorMessage}</div>
                                        )}

                                        <button className="btn-primary sd-btn-flex" onClick={handleGradesSaveContinue}>
                                            {t('specializationSelector.saveContinue')}
                                        </button>
                                    </>
                                ) : (
                                    <button className="btn-primary sd-btn-inline" disabled>
                                        <span style={{display: 'inline-flex', alignItems: 'center', gap: '8px'}}>
                                            <LockIcon size={15} />
                                            {t('settings.gradesLocked')}
                                        </span>
                                    </button>
                                )}
                            </section>

                            <section className={`sd-section ${activeSection === 'preferences' ? 'active' : ''}`}>
                                <header className="sd-panel-header">
                                    <h1>{t('settings.advancedSettings')}</h1>
                                </header>
                                <SavingIndicator active={isSavingPreferences} label={t('common.saving')} />
                                {loading ? (
                                    <p>{t('settings.loadingSettings')}</p>
                                ) : (
                                    <div className="theme-toggle">
                                        <label>{t('settings.theme')}</label>
                                        <select
                                            value={currentTheme}
                                            onChange={(e) => handleThemeChange(e.target.value)}
                                            disabled={isSavingPreferences}
                                            style={{background: 'var(--bg-secondary)', color: 'var(--text-main)', border: '1px solid var(--border)', padding: '8px 12px', borderRadius: '8px'}}
                                        >
                                            <option value="light">{t('settings.light')}</option>
                                            <option value="dark">{t('settings.dark')}</option>
                                        </select>
                                    </div>
                                )}
                                {!loading && (
                                    <div className="theme-toggle" style={{ marginTop: '20px' }}>
                                        <label>{t('settings.notifSound')}</label>
                                        <select
                                            value={notifSound}
                                            onChange={(e) => handleNotifSoundChange(e.target.value)}
                                            disabled={isSavingPreferences}
                                            style={{background: 'var(--bg-secondary)', color: 'var(--text-main)', border: '1px solid var(--border)', padding: '8px 12px', borderRadius: '8px'}}
                                        >
                                            {AVAILABLE_NOTIF_SOUNDS.map((sound) => (
                                                <option key={sound} value={sound}>{sound}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {!loading && (
                                    <div className="theme-toggle" style={{ marginTop: '20px' }}>
                                        <label>{t('settings.muteNotifications')}</label>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={muteNotifications}
                                                onChange={handleMuteToggle}
                                                disabled={isSavingPreferences}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                    </div>
                                )}
                            </section>
                            <section className={`sd-section ${activeSection === 'reports' ? 'active' : ''}`}>
                                <header className="sd-panel-header">
                                    <h1>{t('settings.reports')}</h1>
                                </header>

                                <ReportForm />
                            </section>
                        </div>
                    </div>
                </div>

                {specializationSelectorOpen && (
                    <SpecializationSelector
                        forceStage={specializationSelectorForceStage}
                        initialSelectedGroups={specializationSelectorForceStage === 'verify' ? gradesSelectedGroups : undefined}
                        onClose={closeSpecializationSelector}
                    />
                )}

                <div id="modalChangePassword" className="modal-overlay-profile">
                    <div className="modal-box">
                        <h2 className="section-title">{t('settings.changePassword')}</h2>
                            <div className="form-group">
                                <label className="label-text">{t('settings.currentPassword')}</label>
                                <input
                                    type="password"
                                    className="login-input"
                                    value={passwords.currentPassword}
                                    onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="label-text">{t('settings.newPassword')}</label>
                                <input
                                    type="password"
                                    className="login-input"
                                    value={passwords.newPassword}
                                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="label-text">{t('settings.confirmNewPassword')}</label>
                                <input
                                    type="password"
                                    className="login-input"
                                    value={passwords.confirmPassword}
                                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                                />
                            </div>
                            <div id="passwordAlert" className="alert-msg alert-error"></div>
                            <div style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
                                <button className="btn-primary" style={{marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center'}} onClick={handlePasswordChange}>{t('settings.changePassword')}</button>
                                <button className="btn-primary" style={{background:'var(--bg-secondary)', color:'var(--text-main)', border:'1px solid var(--border)', marginTop: '10px', width: '100%', boxShadow: 'none'}} onClick={() => closeModal('modalChangePassword')}>{t('common.cancel')}</button>
                            </div>
                    </div>
                </div>
                <div id="modalVerifyEmail" className="modal-overlay-profile">
                    <div className="modal-box">
                        <span style={{fontSize: '3.5rem'}}>📩</span>
                        <h2 style={{margin: '15px 0 5px'}}>{t('common.verifyYourEmail')}</h2>
                        <p style={{color: 'var(--text-muted)', lineHeight: 1.5, fontSize: '0.95rem'}}>
                            {t('common.enterCodeSentTo')} <br/><strong id="verifyEmailTarget" style={{color: 'var(--text-main)'}}>...</strong>
                        </p>

                        <div className="code-inputs">
                            <input type="text" className="code-input" id="c1" maxLength="1" onKeyUp={(e) => moveToNext(e.target, 'c2')} />
                            <input type="text" className="code-input" id="c2" maxLength="1" onKeyUp={(e) => moveToNext(e.target, 'c3')} />
                            <input type="text" className="code-input" id="c3" maxLength="1" onKeyUp={(e) => moveToNext(e.target, 'c4')} />
                            <input type="text" className="code-input" id="c4" maxLength="1" onKeyUp={(e) => moveToNext(e.target, 'c5')} />
                            <input type="text" className="code-input" id="c5" maxLength="1" onKeyUp={(e) => moveToNext(e.target, 'c6')} />
                            <input type="text" className="code-input" id="c6" maxLength="1" />
                        </div>

                        <button className="btn-primary" style={{width: '100%'}} onClick={startVerificationProcess}>{t('common.verifyAndLink')}</button>
                        <button className="btn-primary" style={{background:'var(--bg-secondary)', color:'var(--text-main)', border:'1px solid var(--border)', marginTop: '10px', width: '100%', boxShadow: 'none'}} onClick={() => closeModal('modalVerifyEmail')}>{t('common.cancel')}</button>
                    </div>
                </div>

                <div id="modalLoading" className="modal-overlay-profile">
                    <div className="modal-box" style={{padding: '60px 40px'}}>

                        <div id="loadingState">
                            <div className="spinner"></div>
                            <h3 style={{marginTop:'25px', color:'var(--text-main)'}}>{t('common.verifyingCode')}</h3>
                        </div>

                        <div id="successState" style={{display:'none'}}>
                            <div className="icon-pop">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon-svg success-icon" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 6L9 17l-5-5"></path>
                                </svg>
                            </div>
                            <h3 style={{marginTop:'20px', color:'#10b981', fontWeight:800}}>{t('common.success')}</h3>
                            <p style={{color:'var(--text-muted)', fontSize:'0.95rem'}}>{t('common.accountLinkedSuccessfully')}</p>
                        </div>

                        <div id="errorState" style={{display:'none'}}>
                            <div className="icon-shake">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon-svg error-icon" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </div>
                            <h3 style={{marginTop:'20px', color:'#ef4444', fontWeight:800}}>{t('common.invalidCode')}</h3>
                            <p style={{color:'var(--text-muted)', fontSize:'0.95rem'}}>{t('common.checkCodeTryAgain')}</p>
                        </div>

                    </div>
                </div>

                <div id="modalConfirmRemove" className="modal-overlay-profile">
                    <div className="modal-box">
                        <span style={{fontSize: '3rem'}}>⚠️</span>
                        <h2 style={{margin: '15px 0 5px'}}>{t('settings.removeEmailQuestion')}</h2>
                        <p style={{color: 'var(--text-muted)', lineHeight: 1.5, fontSize: '0.95rem'}}>
                            {t('settings.removeEmailConfirm')} <br/><strong id="removeEmailTarget" style={{color: 'var(--text-main)'}}>...</strong>? <br/><br/>{t('settings.removeEmailWarning')}
                        </p>

                        <button className="btn-primary" style={{width: '100%', background: '#ef4444', marginTop: '10px', boxShadow: '0 8px 15px rgba(239, 68, 68, 0.2)'}} onClick={() => executeRemoveMail(emailToRemove)}>{t('settings.yesRemove')}</button>
                        <button className="btn-primary" style={{background:'var(--bg-secondary)', color:'var(--text-main)', border:'1px solid var(--border)', marginTop: '10px', width: '100%', boxShadow: 'none'}} onClick={() => closeModal('modalConfirmRemove')}>{t('common.cancel')}</button>
                    </div>
                </div>

                <button className="btn-primary close-profile" style={{position: 'fixed', bottom: '30px', zIndex: '1000', width: '25%', left: '30px'}} onClick={() => navigate(closePath)}>{t('settings.close')}</button>
            </div>
        </>
    );
}

export default Settings;
